import type { KeyboardEventHandler, ReactElement, ReactNode } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocale } from '../contexts/LocaleContext'
import usePrevious from '../hooks/usePrevious'
import type SvgIcon from '../SvgIcon/SvgIcon'
import { Keys } from '../utilities/keyboard'
import { ActionTypes } from './Actions'
import Body from './Body'
import Head from './Head'
import type {
  ClickRowActionEventHandler,
  TableColumn,
  TableRow,
} from './TableContext'
import { useTable } from './TableContext'

export interface BaseTableProps {
  children?: ReactElement | ReactElement[]
  columns: TableColumn[]
  dense?: boolean
  loading?: boolean
  rowActions?: {
    enabled?: (row: TableRow) => boolean
    label: ReactNode
    handler: ClickRowActionEventHandler
    startAdornment?: typeof SvgIcon
  }[]
  rows: TableRow[]
  scrollable?: boolean
  selectable?: boolean
  storageKey?: string
  total?: number
}

function BaseTable({
  children,
  columns: initialColumns,
  dense = false,
  loading = false,
  rowActions,
  rows,
  scrollable = true,
  selectable = false,
  storageKey,
  total,
}: BaseTableProps): JSX.Element {
  const header = useRef<HTMLElement | null>(null)
  const tableContainer = useRef<HTMLDivElement | null>(null)
  const [headerBottomPosition, setHeaderBottomPosition] = useState(0)
  const [locale] = useLocale()
  const previousLocale = usePrevious(locale)
  const [state, dispatch] = useTable()

  const handleScroll = useCallback(() => {
    if (!tableContainer.current || !state.scrollable) {
      return
    }

    dispatch({
      type: ActionTypes.UpdateScrolling,
      isScrolling: !(tableContainer.current.scrollTop === 0),
    })
  }, [dispatch, state.scrollable])

  const getHeaderBottomPosition = () => {
    if (header.current) {
      setHeaderBottomPosition(header.current.getBoundingClientRect().bottom)
    }
  }

  useEffect(() => {
    const container = tableContainer.current
    getHeaderBottomPosition()

    if (state.scrollable) {
      container?.addEventListener('scroll', handleScroll)
    }
    window.addEventListener('resize', getHeaderBottomPosition)

    return () => {
      if (state.scrollable) {
        container?.removeEventListener('scroll', handleScroll)
      }
      window.removeEventListener('resize', getHeaderBottomPosition)
    }
  }, [handleScroll, state.scrollable])

  useEffect(() => {
    if (locale === previousLocale) {
      return
    }

    if (initialColumns.some((column) => column.filter !== undefined)) {
      const columns = { ...state.columns }
      const keys = ['selected', 'unselected'] as const

      initialColumns.forEach((initialColumn) => {
        keys.forEach((key) => {
          const index = columns[key].findIndex(
            (column) => column.key === initialColumn.key && column.filter,
          )

          if (index > -1) {
            const column = columns[key][index]

            if (column.filter && initialColumn.filter) {
              column.filter = column.filter.map((filter) => ({
                ...filter,
                label:
                  initialColumn.filter?.find(
                    (initialFilter) => filter.value === initialFilter.value,
                  )?.label ?? filter.label,
              }))

              columns[key][index] = column
            }
          }
        })
      })

      dispatch({
        type: ActionTypes.UpdateColumns,
        columns,
      })

      if (state.selectable) {
        const index = columns.selected.findIndex(
          (column) => column.key === 'checkbox',
        )

        columns.selected = [
          ...columns.selected.slice(0, index),
          ...columns.selected.slice(index + 1),
        ]
      }

      if (storageKey) {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify([...columns.selected, ...columns.unselected]),
        )
      }
    }
  }, [
    dispatch,
    initialColumns,
    locale,
    previousLocale,
    state.selectable,
    state.columns,
    storageKey,
  ])

  useEffect(() => {
    if (storageKey) {
      const columns = { ...state.columns }

      if (state.selectable) {
        const index = columns.selected.findIndex(
          (column) => column.key === 'checkbox',
        )

        columns.selected = [
          ...columns.selected.slice(0, index),
          ...columns.selected.slice(index + 1),
        ]
      }

      window.localStorage.setItem(
        storageKey,
        JSON.stringify([...columns.selected, ...columns.unselected]),
      )
    }
  }, [state.selectable, state.columns, storageKey])

  useEffect(() => {
    dispatch({
      type: ActionTypes.UpdateRows,
      rows,
      selectionsRef: state.selectionsRef,
      total,
    })
  }, [dispatch, rows, state.selectionsRef, total])

  useEffect(() => {
    dispatch({
      type: ActionTypes.UpdateRowActions,
      rowActions,
    })
  }, [dispatch, rowActions])

  useEffect(() => {
    dispatch({
      type: ActionTypes.UpdateLoading,
      loading,
    })
  }, [dispatch, loading])

  useEffect(() => {
    dispatch({
      type: ActionTypes.UpdateScrollable,
      scrollable,
    })
  }, [dispatch, scrollable])

  useEffect(() => {
    dispatch({
      type: ActionTypes.UpdateDense,
      dense,
    })
  }, [dense, dispatch])

  useEffect(() => {
    dispatch({
      type: ActionTypes.UpdateSelectable,
      selectable,
    })
  }, [dispatch, selectable])

  const handleKeyDown: KeyboardEventHandler<HTMLElement> = (event) => {
    const cell = event.target as
      | HTMLTableCellElement
      | HTMLTableHeaderCellElement
    const row = cell.parentElement as HTMLTableRowElement
    const table = row.parentElement?.parentElement as HTMLTableElement

    const cellIndex = cell.cellIndex
    const rowIndex = row.rowIndex

    if (cellIndex === undefined || rowIndex === undefined) {
      return
    }

    let newCell

    switch (event.key) {
      case Keys.ArrowDown:
        newCell = table.rows[rowIndex + 1]?.cells[cellIndex]
        break
      case Keys.ArrowUp:
        newCell = table.rows[rowIndex - 1]?.cells[cellIndex]
        break
      case Keys.ArrowRight:
        newCell = table.rows[rowIndex].cells[cellIndex + 1]
        break
      case Keys.ArrowLeft:
        newCell = table.rows[rowIndex].cells[cellIndex - 1]
        break
    }

    if (newCell) {
      newCell.focus()
      newCell.setAttribute('tabIndex', '0')
      cell.setAttribute('tabIndex', '-1')
    }
  }

  const viewportHeight = window.innerHeight
  const headerHeight = header.current
    ? header.current.getBoundingClientRect().height
    : 0

  // Tables within the viewport have heights that rely on the bottom position of the header.
  // However, to give other tables (beyond the viewport) the same height, we need to rely on the height of the header instead.
  const isBelowViewport = headerBottomPosition > viewportHeight

  // Calculate maxHeight only if scrollable is enabled
  const maxHeight = scrollable
    ? isBelowViewport
      ? `calc(${viewportHeight - headerHeight}px)`
      : `calc(${viewportHeight - headerBottomPosition}px)`
    : undefined

  return (
    <div>
      <header className="divide-y divide-black-10 bg-white-100" ref={header}>
        {children}
      </header>
      <section className="w-full max-w-full">
        <div
          className={scrollable ? 'overflow-auto' : ''}
          ref={tableContainer}
          style={{ maxHeight }}
        >
          <table
            cellPadding={0}
            className="w-full font-sans border-spacing-0"
            onKeyDown={handleKeyDown}
            role="grid"
          >
            <Head />
            <Body />
          </table>
        </div>
      </section>
    </div>
  )
}

export default BaseTable
